import axios from 'axios';
import httpClient from '@/src/lib/api/http/client';
import { ApplicationError } from '@/src/shared/errors';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { DGetComplaintContact } from '../data/dtos/contact';
import { DGetComplaintConversation } from '../data/dtos/conversation';
import { DUpdateComplaintLastSeen } from '../data/dtos/last-seen';
import {
  DCreateComplaintMessage,
  DGetComplaintMessages,
} from '../data/dtos/message';
import {
  mapChatContactSchemaToChatContactModel,
  mapChatConversationSchemaToChatConversationModel,
  mapChatMessageSchemaToChatMessageModel,
} from '../data/mappers';
import {
  SChatContactResponse,
  TChatContactResponse,
} from '../data/schemas/api/responses/chat-contact';
import {
  SChatConversationResponse,
  TChatConversationResponse,
} from '../data/schemas/api/responses/chat-conversation';
import {
  SChatMessageResponse,
  SChatMessagesResponse,
  TChatMessageResponse,
  TChatMessagesResponse,
} from '../data/schemas/api/responses/chat-message';
import {
  SUpdateLastSeenResponse,
  TUpdateLastSeenResponse,
} from '../data/schemas/api/responses/last-seen';
import {
  GetContactFailedError,
  GetConversationsFailedError,
  GetMessagesFailedError,
  SendMessageFailedError,
  UpdateLastSeenFailedError,
} from '../errors';

export const complaintChatService = {
  getUserConversation: async ({ userId }: DGetComplaintConversation) => {
    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/complaint/users/${userId}/conversation`
      );

      const responseSchema = safeParseZodObject<TChatConversationResponse>(
        SChatConversationResponse,
        response.data
      );
      return mapChatConversationSchemaToChatConversationModel(
        responseSchema.data
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetConversationsFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getUserContact: async ({ userId }: DGetComplaintContact) => {
    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/complaint/users/${userId}/contact`
      );

      const responseSchema = safeParseZodObject<TChatContactResponse>(
        SChatContactResponse,
        response.data
      );
      return mapChatContactSchemaToChatContactModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetContactFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getMessages: async ({ userId, lastMessageId }: DGetComplaintMessages) => {
    const params = {
      ...(lastMessageId && { last_message_id: lastMessageId }),
    };

    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/complaint/users/${userId}/conversation/messages`,
        { params }
      );

      const responseSchema = safeParseZodObject<TChatMessagesResponse>(
        SChatMessagesResponse,
        response.data
      );
      return responseSchema.data.map((message) => {
        return mapChatMessageSchemaToChatMessageModel(message);
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetMessagesFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  sendMessage: async ({
    userId,
    content,
    attachments,
  }: DCreateComplaintMessage) => {
    const request = new FormData();
    if (content) {
      request.append('content', content);
    }
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        request.append('attachments', file);
      });
    }

    try {
      const response = await httpClient.healthyoneMarketplace.post(
        `/chat/complaint/users/${userId}/conversation/messages`,
        request
      );
      const responseSchema = safeParseZodObject<TChatMessageResponse>(
        SChatMessageResponse,
        response.data
      );
      return mapChatMessageSchemaToChatMessageModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new SendMessageFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  updateLastSeen: async ({ userId }: DUpdateComplaintLastSeen) => {
    try {
      const response = await httpClient.healthyoneMarketplace.patch(
        `/chat/complaint/users/${userId}/conversation/last-seen`
      );
      const responseSchema = safeParseZodObject<TUpdateLastSeenResponse>(
        SUpdateLastSeenResponse,
        response.data
      );
      return responseSchema.data.last_seen;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new UpdateLastSeenFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },
};
